import React, { useEffect, useState } from 'react';
import { useApolloClientForAspirex } from '@frontend/applications/AffiliatesApp/hooks/useApolloClientForAspirex';
import { GET_NODE_OPTIONS_QUERY } from '@frontend/app/queries';
import { GetNodeOptions_getNodeOptions_conditionOptions } from '@frontend/app/queries/types/GetNodeOptions';
import { Button } from '@/shadcn/components/ui/button';
import AdvancedSelectionCondition from './AdvancedSelectionCondition';
import { Condition, ConditionOption } from './BoostTypes';

interface AdvancedSelectionFormProps {
  value: Condition[];
  onChange: (val: Condition[]) => void;
}

type Mode = 'add' | 'edit' | 'display';

const AdvancedSelectionForm: React.FC<AdvancedSelectionFormProps> = ({ value, onChange }) => {
  const [mode, setMode] = useState<Mode>('display');
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [conditionOptions, setConditionOptions] = useState<ConditionOption[]>([]);
  const aspirexApolloClient = useApolloClientForAspirex();
  useEffect(() => {
    if (aspirexApolloClient) {
      (async () => {
        try {
        const { data } = await aspirexApolloClient.query({
          query: GET_NODE_OPTIONS_QUERY,
        });
        if (data?.getNodeOptions?.conditionOptions) {
          setConditionOptions(data.getNodeOptions.conditionOptions.map((option: GetNodeOptions_getNodeOptions_conditionOptions) => ({
            id: option.id,
            displayName: option.displayName,
            attributeType: option.attributeType,
            group: option.group
          })));
        }
        } catch (error) {
          console.log(error);
        }
      })();
    }
  }, [aspirexApolloClient]);
  function handleAdd() {
    setMode('add');
    setEditIndex(null);
  }

  function handleEdit(idx: number) {
    setMode('edit');
    setEditIndex(idx);
  }

  function handleCancel() {
    setMode('display');
    setEditIndex(null);
  }

  function handleSave(index: number | null, condition: Condition) {
    if (mode === 'add') {
      onChange([...value, condition]);
    } else if (mode === 'edit' && index !== null) {
      onChange(value.map((c, i) => (i === index ? condition : c)));
    }
    setMode('display');
    setEditIndex(null);
  }

  function handleDelete(index: number) {
    onChange(value.filter((_, i) => i !== index));
    setMode('display');
    setEditIndex(null);
  }

  return (
    <div className="flex flex-col gap-2">
      {value.map((cond, idx) => (
        <AdvancedSelectionCondition
          key={idx}
          mode={mode === 'edit' && editIndex === idx ? 'edit' : 'display'}
          condition={cond}
          index={idx}
          onEdit={() => handleEdit(idx)}
          onDelete={() => handleDelete(idx)}
          onSave={handleSave}
          onCancel={handleCancel}
          conditionOptions={conditionOptions}
        />
      ))}
      {mode === 'add' && (
        <AdvancedSelectionCondition
          key="new"
          mode="add"
          condition={null}
          index={null}
          onEdit={() => {}}
          onDelete={() => {}}
          onSave={handleSave}
          onCancel={handleCancel}
          conditionOptions={conditionOptions}
        />
      )}
      {mode === 'display' && (
        <Button variant="ghost" size="sm" onClick={handleAdd} className="justify-start px-0 hover:underline hover:bg-transparent">
          + Add
        </Button>
      )}
    </div>
  );
};

export default AdvancedSelectionForm;
